import React, { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row
} from 'reactstrap'
import { IBoothRecord } from './index'
import { pick } from 'lodash'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { isEqual } from 'lodash'
// Hizmet azaltma özeti — saf ve test edilmiş; bkz. serviceChange.test.ts
import { summarizeServiceChange } from './serviceChange'

interface IProps {
  boothRecord: IBoothRecord | undefined
  isOpen: boolean
  editMode: boolean
  actionOptions: any[]
  currencyOptions: any[]
  languageOptions: any[]
  locationOptions: any[]
  setOpenPopup: (arg: boolean) => void
  handleCreateOrUpdateBooth: (arg: any) => Promise<string>
  canEdit: boolean
}

interface IBoothRecordState {
  id?: string | number
  _id?: string // MongoDB ID
  name: string
  locationId: string | number | undefined | null
  activeActions: string[]
  isCash: boolean
  isCreditCard: boolean
  currencyId: string | number | null
  languageId: string | number | null
  isPrinterActive: boolean
  isActive: boolean
}

const initBoothRecord: IBoothRecordState = {
  name: '',
  locationId: '',
  activeActions: [],
  isCash: true,
  isCreditCard: true,
  languageId: 'tr',
  currencyId: 'TRY',
  isPrinterActive: true,
  isActive: true
}

const BoothPopup: React.FC<IProps> = ({
  boothRecord,
  isOpen,
  actionOptions,
  currencyOptions,
  languageOptions,
  locationOptions,
  editMode,
  handleCreateOrUpdateBooth,
  setOpenPopup,
  canEdit
}) => {
  const [boothRecordState, setBoothRecordState] = useState<IBoothRecordState>(initBoothRecord)
  const [initBoothRecordState, setInitBoothRecordState] =
    useState<IBoothRecordState>(initBoothRecord)
  const [disableSave, setDisableSave] = useState<boolean>(editMode)
  // Kapanacak hizmetlerin adları; doluysa onay kutusu açıktır (2026-08-28).
  const [pendingRemoval, setPendingRemoval] = useState<string[] | null>(null)
  const hasEdit = (editMode && boothRecordState) || false

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: boothRecordState?.name || '',
      locationId: boothRecordState?.locationId || '',
      currencyId: boothRecordState?.currencyId || initBoothRecord.currencyId,
      languageId: boothRecordState?.languageId || initBoothRecord.languageId
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please Enter Name'),
      locationId: Yup.string().required('Please Select Location'),
      currencyId: Yup.string().required('Please Select Currency'),
      languageId: Yup.string().required('Please Select Language')
    }),
    onSubmit: (values) => {
      handleSubmit()
    }
  })

  useEffect(() => {
    if (editMode && boothRecord) {
      // Extract IDs from populated objects (MongoDB returns full objects when populated)
      // Or use direct string values for currency/language (MongoDB stores them as strings like 'TRY', 'tr')
      const locationId = typeof boothRecord.location === 'object' 
        ? (boothRecord.location as any)?._id 
        : boothRecord.locationId
      
      // Currency and language might be strings directly ('TRY', 'tr') or objects with _id
      // Use currency/language directly if they're strings, otherwise extract _id
      const currencyId = typeof boothRecord.currency === 'string'
        ? boothRecord.currency  // MongoDB stores as 'TRY' string
        : (boothRecord.currency as any)?._id || boothRecord.currencyId
      const languageId = typeof boothRecord.language === 'string'
        ? boothRecord.language  // MongoDB stores as 'tr' string
        : (boothRecord.language as any)?._id || boothRecord.languageId

      // Map nested paymentMethods to flat booleans for modal state
      const pm = (boothRecord as any).paymentMethods
      const isCash = pm ? pm.cash === true : (boothRecord as any).isCash === true
      const isCreditCard = pm ? pm.creditCard === true : (boothRecord as any).isCreditCard === true

      const stateToSet = {
        ...boothRecord,
        locationId: locationId || '',
        currencyId: currencyId || '',
        languageId: languageId || '',
        isCash,
        isCreditCard,
        activeActions:
          boothRecord.activeActions && Array.isArray(boothRecord.activeActions)
            ? boothRecord.activeActions.map((a: any) => a._id || a)
            : []
      }
      setBoothRecordState(stateToSet)
      setInitBoothRecordState(stateToSet)
      setDisableSave(true)
    } else if (editMode) {
      // 🔴 DÜZENLEME KİPİNDE KAYIT YOK.
      // Buradan aşağıdaki "yeni kabin" dalına düşmek TEHLİKELİDİR: form,
      // kullanıcının düzenlediğini sandığı kabine VARSAYILAN hizmet listesini
      // yükler ve kaydedilirse kabinin gerçek hizmetleri değişir. 28 Ağustos'ta
      // Atlaspark'ın hizmetlerinin sessizce değişmesinin muhtemel yolu buydu.
      // Doğru davranış: hiçbir şey yükleme, kaydetmeyi kapalı tut.
      setDisableSave(true)
    } else {
      // Set default active actions: buyulu_selfie and cepten_baski
      const defaultActionNames = ['buyulu_selfie', 'cepten_baski']
      const defaultActionIds = actionOptions
        .filter((a: any) => defaultActionNames.includes(a.name))
        .map((a: any) => a._id)
      setBoothRecordState({ ...initBoothRecord, activeActions: defaultActionIds })
      setDisableSave(false)
    }

    if (isOpen) {
      formik.resetForm()
    }
    // Modal her açılışta temiz gelmeli: önceki kabinden kalan onay kutusu
    // yanlış hizmet adlarını gösterirdi.
    setPendingRemoval(null)
  }, [isOpen])

  const togglePopup = () => {
    setOpenPopup(!isOpen)
  }

  useEffect(() => {
    setDisableSave(isEqual(boothRecordState, initBoothRecordState))
  }, [boothRecordState])

  const paymentMethodOptions = [
    { label: 'Cash', value: 'Cash' },
    { label: 'Credit Card', value: 'CreditCard' }
  ]

  const handleOnChange = (e: { target: { name: any; value: any } }) => {
    const name = e.target.name
    const value = e.target.value
    setBoothRecordState({
      ...boothRecordState,
      [name]: value
    })
    formik.setFieldValue(name, value)
  }

  const handleChangeActiveAction = (id: string, checked: boolean) => {
    let newActions = [...(boothRecordState.activeActions || [])]
    if (checked) {
      if (!newActions.includes(id)) newActions.push(id)
    } else {
      newActions = newActions.filter((a) => a !== id)
    }
    setBoothRecordState({
      ...boothRecordState,
      activeActions: newActions
    })
  }

  const handleChangePaymentMethod = (value: string, checked: boolean) => {
    setBoothRecordState({
      ...boothRecordState,
      [`is${value}`]: Boolean(checked)
    })
  }

  const handleChangePrinterActive = (checked: boolean) => {
    setBoothRecordState({
      ...boothRecordState,
      isPrinterActive: checked
    })
  }

  const handleChangeBoothActive = (checked: boolean) => {
    setBoothRecordState({
      ...boothRecordState,
      isActive: checked
    })
  }

  /**
   * Kaydetmeden ÖNCEKİ hizmet kontrolü (saha olayı 2026-08-28).
   *
   * O gün bu ekrandan yapılan tek bir "Save" Atlaspark'ı 3 hizmetten 1'e
   * düşürdü; ekranda hiçbir uyarı çıkmadı, kaydetme başarılı göründü ve kabin
   * 19 saat satış yapamadı. Kapanacak hizmetler kaydetmeden önce yazıyla
   * gösterilseydi olay yaşanmazdı.
   *
   * Sunucu bu kararın OTORİTESİDİR (hizmetsiz bırakan istek 400 döner);
   * buradaki kontrol yalnızca kullanıcıyı erken uyarır.
   */
  const handleSubmit = async () => {
    const ozet = summarizeServiceChange(
      initBoothRecordState.activeActions || [],
      boothRecordState.activeActions || [],
      actionOptions
    )

    if (editMode && ozet.wouldEmpty) {
      toast.error(
        'Kabin hizmetsiz bırakılamaz: en az bir hizmet seçili kalmalı. ' +
          'Kabini geçici olarak kapatmak için "Bakıma Al" düğmesini kullanın.'
      )
      return
    }

    if (editMode && ozet.isReduction) {
      setPendingRemoval(ozet.removedLabels)
      return
    }

    await saveBooth()
  }

  const saveBooth = async () => {
    setPendingRemoval(null)
    const picked = pick(boothRecordState, Object.keys(initBoothRecord))
    const bodyParams: any = {
      name: picked.name,
      location: picked.locationId,
      currency: picked.currencyId,
      language: picked.languageId,
      activeActions: picked.activeActions,
      paymentMethods: {
        cash: picked.isCash,
        creditCard: picked.isCreditCard,
        qrcode: false,
      },
      isPrinterActive: picked.isPrinterActive,
      isActive: picked.isActive,
    }
    if (editMode) {
      bodyParams.id = boothRecordState._id || boothRecordState.id
    }
    const res = handleCreateOrUpdateBooth(bodyParams)
    const message = await res
    if (message === 'SUCCESSFULL') {
      toast.success(editMode ? 'Updated a booth successfully' : 'Created a new booth successfully')
      setOpenPopup(false)
    }
  }

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          togglePopup()
        }}
        scrollable={true}
        id='boothModal'
      >
        <ModalHeader
          className='modal-title'
          id='boothModalTitle'
          toggle={() => {
            togglePopup()
          }}
        >
          {editMode ? 'Booth Record Details' : 'Create A New Booth Record'}
        </ModalHeader>
        <ModalBody>
          <Form
            className='needs-validation'
            onSubmit={(e) => {
              e.preventDefault()
              formik.handleSubmit()
              return false
            }}
          >
            <Row>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div>
                    <Label htmlFor='name' className='form-label mb-1 fw-semibold'>
                      Name
                    </Label>
                    <Input
                      type='text'
                      maxlength='50'
                      className='form-control'
                      id='name'
                      name='name'
                      placeholder='Name'
                      value={boothRecordState?.name}
                      onChange={(e) => handleOnChange(e)}
                      invalid={formik.touched.name && formik.errors.name ? true : false}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <FormFeedback type='invalid'>{formik.errors.name}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </FormGroup>
              {/* Location */}
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div>
                    <Label htmlFor='location' className='form-label mb-1 fw-semibold'>
                      Location
                    </Label>
                    <select
                      className={`form-select ${formik.touched.locationId && formik.errors.locationId ? 'is-invalid' : ''}`}
                      name='locationId'
                      value={boothRecordState?.locationId || ''}
                      onChange={(e) => handleOnChange(e)}
                    >
                      <option value=''>Select location</option>
                      {locationOptions.map((location, index) => {
                        return (
                          <option
                            key={index}
                            value={location._id || location.id}
                          >
                            {location.name} - {location.address}, {location.city}
                          </option>
                        )
                      })}
                    </select>
                    {formik.touched.locationId && formik.errors.locationId ? (
                      <FormFeedback type='invalid'>{formik.errors.locationId}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </FormGroup>
              {/* Active Action */}
              <Col md={12} className='mb-3'>
                <div>
                  <Label htmlFor='active-action' className='form-label mb-1 fw-semibold'>
                    Active Action
                  </Label>
                  <Row>
                    {actionOptions.map((action, index) => {
                      const checked =
                        boothRecordState.activeActions &&
                        boothRecordState.activeActions.includes(action._id)
                      return (
                        <Col md={4} key={index}>
                          <div className='form-check'>
                            <Input
                              id={`active-action-option-${index}`}
                              type='checkbox'
                              className='form-check-input'
                              checked={checked}
                              onChange={(e) =>
                                handleChangeActiveAction(action._id, e.target.checked)
                              }
                            />
                            <Label
                              className='form-check-label fw-normal'
                              htmlFor={`active-action-option-${index}`}
                            >
                              {action.label || action.name}
                            </Label>
                          </div>
                        </Col>
                      )
                    })}
                  </Row>
                </div>
              </Col>
              {/* Valid Payment Method */}
              <Col md={12} className='mb-3'>
                <div>
                  <Label htmlFor='active-action' className='form-label mb-1 fw-semibold'>
                    Valid Payment Method
                  </Label>
                  <Row>
                    {paymentMethodOptions.map((method, index) => {
                      const tempState = boothRecordState as any
                      const checked = tempState[`is${method.value}`]
                      return (
                        <Col md={4} key={index}>
                          <div className='form-check'>
                            <Input
                              className='form-check-input'
                              type='checkbox'
                              id={`payment-method-option-${index}`}
                              checked={checked}
                              onChange={(e) =>
                                handleChangePaymentMethod(method.value, e.target.checked)
                              }
                            />
                            <Label
                              className='form-check-label fw-normal'
                              htmlFor={`payment-method-option-${index}`}
                            >
                              {method.label}
                            </Label>
                          </div>
                        </Col>
                      )
                    })}
                  </Row>
                </div>
              </Col>
              {/* Default Currency */}
              <Col md={6} className='mb-3'>
                <FormGroup>
                  <div>
                    <Label htmlFor='active-action' className='form-label mb-1 fw-semibold'>
                      Default Currency
                    </Label>
                    <select
                      className={`form-select ${formik.touched.currencyId && formik.errors.currencyId ? 'is-invalid' : ''}`}
                      aria-label='Default currency'
                      name='currencyId'
                      value={boothRecordState?.currencyId || ''}
                      onChange={(e) => handleOnChange(e)}
                    >
                      <option value=''>Select default currentcy </option>
                      {currencyOptions.map((currency, index) => {
                        // MongoDB stores currency as symbol string like 'TRY'
                        const value = currency.symbol || currency._id || currency.id
                        return (
                          <option
                            key={index}
                            value={value}
                          >
                            {currency.name} ({currency.symbol})
                          </option>
                        )
                      })}
                    </select>
                    {formik.touched.currencyId && formik.errors.currencyId ? (
                      <FormFeedback type='invalid'>{formik.errors.currencyId}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              {/* Default Language */}
              <Col md={6} className='mb-3'>
                <FormGroup>
                  <div>
                    <Label htmlFor='active-action' className='form-label mb-1 fw-semibold'>
                      Default Languge
                    </Label>
                    <select
                      className={`form-select ${formik.touched.languageId && formik.errors.languageId ? 'is-invalid' : ''}`}
                      aria-label='Default language'
                      name='languageId'
                      value={boothRecordState?.languageId || ''}
                      onChange={(e) => handleOnChange(e)}
                    >
                      <option value=''>Select default language</option>
                      {languageOptions.map((lang, index) => {
                        // MongoDB stores language as code string like 'tr', 'en'
                        // Map common language names to codes
                        const codeMap: Record<string, string> = {
                          'Turkish': 'tr', 'English': 'en', 'German': 'de', 
                          'French': 'fr', 'Spanish': 'es', 'Arabic': 'ar'
                        }
                        const value = lang.code || codeMap[lang.name] || lang._id || lang.id
                        return (
                          <option
                            key={index}
                            value={value}
                          >
                            {lang.name}
                          </option>
                        )
                      })}
                    </select>
                    {formik.touched.languageId && formik.errors.languageId ? (
                      <FormFeedback type='invalid'>{formik.errors.languageId}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              {/* IP Address - read-only, auto-filled by heartbeat */}
              {editMode && boothRecord?.connectionStatus?.ip && (
                <Col md={6} className='mb-3'>
                  <div>
                    <Label htmlFor='ip-address' className='form-label mb-1 fw-semibold'>
                      IP Address
                    </Label>
                    <Input
                      type='text'
                      className='form-control'
                      id='ip-address'
                      value={boothRecord.connectionStatus.ip}
                      disabled
                    />
                  </div>
                </Col>
              )}
              <Col md={12} className='mb-3'>
                <div className='form-check'>
                  <Input
                    className='form-check-input'
                    type='checkbox'
                    id='printer-active'
                    checked={boothRecordState.isPrinterActive}
                    onChange={(e) => handleChangePrinterActive(e.target.checked)}
                  />
                  <Label className='form-check-label fw-medium' htmlFor={`printer-active`}>
                    Printer Active
                  </Label>
                </div>
              </Col>
              <Col md={12} className='mb-3'>
                <div className='form-check'>
                  <Input
                    className='form-check-input'
                    type='checkbox'
                    id='booth-active'
                    checked={boothRecordState.isActive}
                    onChange={(e) => handleChangeBoothActive(e.target.checked)}
                  />
                  <Label className='form-check-label fw-medium' htmlFor={`booth-active`}>
                    Booth Active
                  </Label>
                </div>
              </Col>
            </Row>
            <div className='modal-footer'>
              <Button color='light' onClick={() => setOpenPopup(false)}>
                Close
              </Button>
              <Button color='primary' type='submit' disabled={disableSave}>
                Save
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/*
        HİZMET KAPATMA ONAYI (saha olayı 2026-08-28).
        O gün bu ekrandan yapılan bir kaydetme Atlaspark'ı 3 hizmetten 1'e
        düşürdü ve kabin 19 saat satış yapamadı. Uyarı SAYIYI değil ADLARI
        gösterir: "2 hizmet kapanacak" cümlesi yanlış hizmeti kapattığını
        fark ettirmez, "Photo Roll kapanacak" fark ettirir.
      */}
      <Modal isOpen={!!pendingRemoval} toggle={() => setPendingRemoval(null)} centered>
        <ModalHeader toggle={() => setPendingRemoval(null)}>
          Hizmet kapatılacak — onaylıyor musunuz?
        </ModalHeader>
        <ModalBody>
          <p className='mb-2'>
            <strong>{boothRecordState.name}</strong> kabininde aşağıdaki hizmet
            {(pendingRemoval?.length || 0) > 1 ? 'ler' : ''} kapanacak ve müşteriler bu
            hizmet{(pendingRemoval?.length || 0) > 1 ? 'leri' : 'i'} kabinde{' '}
            <strong>göremeyecek</strong>:
          </p>
          <ul className='mb-3'>
            {(pendingRemoval || []).map((ad) => (
              <li key={ad}>
                <strong>{ad}</strong>
              </li>
            ))}
          </ul>
          <p className='text-muted mb-0'>
            Kabini geçici olarak kapatmak istiyorsanız hizmeti kaldırmak yerine{' '}
            <strong>Bakıma Al</strong> düğmesini kullanın — hizmet listede kalır, müşteri
            "bakımdayız" mesajını görür.
          </p>
        </ModalBody>
        <div className='modal-footer'>
          <Button color='light' onClick={() => setPendingRemoval(null)}>
            Vazgeç
          </Button>
          <Button color='danger' onClick={saveBooth}>
            Evet, kapat
          </Button>
        </div>
      </Modal>
    </React.Fragment>
  )
}
export default BoothPopup
